import { getErrorMessage } from "@utils/getErrorMessage";
import { createResource, createSignal, onCleanup } from "solid-js";
import {
	type ArtifactListResponse,
	type ArtifactListResponseItem,
	listArtifacts,
	toggleArtifactFavourite,
} from "@/client";
import {
	type ArtifactsListQueryCache,
	artifactsListQueryCacheKey,
} from "../list/artifactsListQueryCache";
import { refetchUntilItemCountIncreases } from "./refetchUntilItemCountIncreases";

const fetchArtifacts = async (params: {
	query?: string;
	kind?: string;
	tags?: string;
	favourite?: boolean;
	limit?: number;
	offset?: number;
	question_id?: string;
	/**
	 * Merges each question's answers, newest first. Each AskPage follow-up
	 * is its own conversation, and the endpoint filters by one at a time.
	 */
	question_ids?: string[];
	project_id?: string;
	pool_id?: string;
	agency_id?: string;
	dates?: {
		from?: string;
		to?: string;
	};
	exclude_public_pools?: boolean;
	exclude_studio?: boolean;
}): Promise<ArtifactListResponse> => {
	if (params.question_ids) {
		const { question_ids, ...rest } = params;
		const pages = await Promise.all(
			question_ids.map((question_id) => fetchArtifacts({ ...rest, question_id })),
		);
		const items = pages
			.flatMap((page) => page.items ?? [])
			.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
		return { items, total: items.length };
	}
	const response = await listArtifacts({
		query: {
			query: params.query,
			kind: params.kind as ArtifactListResponseItem["kind"],
			tags: params.tags,
			favourite: params.favourite,
			limit: params.limit ?? 50,
			offset: params.offset ?? 0,
			question_id: params.question_id,
			project_id: params.project_id,
			pool_id: params.pool_id,
			agency_id: params.agency_id,
			created_before: params.dates?.to,
			created_after: params.dates?.from,
			exclude_public_pools: params.exclude_public_pools ?? false,
			exclude_studio: params.exclude_studio ?? false,
		},
	});
	if (response.error) {
		throw new Error(
			getErrorMessage(response.error, "Failed to load artifacts"),
		);
	}
	if (response.response && !response.response.ok) {
		throw new Error(
			getErrorMessage(response.error, "Failed to load artifacts"),
		);
	}
	return response.data ?? { items: [], total: 0 };
};

export const useArtifactQuery = (
	queryArgs: () => false | Parameters<typeof fetchArtifacts>[0],
	cache?: ArtifactsListQueryCache<ArtifactListResponse>,
) => {
	// Once an item's title/summary have been seen in a response, remember and
	// backfill them into every later one. The list endpoint has been observed
	// to occasionally answer a later request with a staler view than an
	// earlier one (e.g. an item that had a title losing it again), which
	// would otherwise both flicker the UI and stop `hasTitle` below from ever
	// seeing the title consistently enough to end its poll early.
	const knownMetadata = new Map<
		string,
		Partial<Pick<ArtifactListResponseItem, "title" | "summary">>
	>();

	const backfillKnownMetadata = (
		result: ArtifactListResponse,
	): ArtifactListResponse => {
		if (!result.items) {
			return result;
		}
		return {
			...result,
			items: result.items.map((item) => {
				if (!item.public_id) {
					return item;
				}
				if (item.title) {
					knownMetadata.set(item.public_id, {
						title: item.title,
						summary: item.summary ?? undefined,
					});
					return item;
				}
				const known = knownMetadata.get(item.public_id);
				return known ? { ...item, ...known } : item;
			}),
		};
	};

	const fetchWithCache = async (
		params: Parameters<typeof fetchArtifacts>[0],
	): Promise<ArtifactListResponse> => {
		const key = artifactsListQueryCacheKey(params);
		const cached = cache?.get(key);
		if (cached) {
			return backfillKnownMetadata(cached);
		}

		const result = await fetchArtifacts(params);
		cache?.set(key, result);
		return backfillKnownMetadata(result);
	};

	const [artifactList, { mutate, refetch }] = createResource(
		queryArgs,
		fetchWithCache,
	);

	const toggleFavourite = async (document_id: string) => {
		mutate((prev) => {
			// The resource has no data when another view (e.g. grouped) is
			// active; leave it untouched so this never throws and the API
			// call below always runs.
			if (!prev?.items) {
				return prev;
			}
			const next = {
				...prev,
				items: prev.items.map((i) =>
					i.public_id === document_id
						? { ...i, is_favourite: !i.is_favourite }
						: i,
				),
			};
			const params = queryArgs();
			if (params !== false && cache) {
				cache.set(artifactsListQueryCacheKey(params), next);
			}
			return next;
		});
		await toggleArtifactFavourite({ path: { document_id } });
	};

	// A saved artifact's title/summary are generated server-side after the
	// save completes, over an indeterminate delay. Rather than a timer per
	// pending item, one shared poll loop keeps refetching the whole list
	// (already the source of truth) as long as *any* item is still waiting -
	// so 10 saves in quick succession still cost one shared timer, not ten.
	// Each item gets its own deadline so a burst of saves doesn't cut a
	// later one's wait short just because an earlier one's clock is running
	// out; once an item's deadline passes with no title, it's dropped and
	// falls back to the normal "untitled" display like any other item.
	const PENDING_POLL_INTERVAL_MS = 2000;
	const PENDING_MAX_WAIT_MS = 10000;
	const [pendingDeadlines, setPendingDeadlines] = createSignal<
		Map<string, number>
	>(new Map());
	let pollTimer: ReturnType<typeof setInterval> | undefined;
	let refreshAborted = false;

	const stopPolling = () => {
		if (pollTimer !== undefined) {
			clearInterval(pollTimer);
			pollTimer = undefined;
		}
	};
	onCleanup(() => {
		refreshAborted = true;
		stopPolling();
	});

	const hasTitle = (document_id: string): boolean =>
		Boolean(
			artifactList()?.items?.find((item) => item.public_id === document_id)?.title,
		);

	// Drops any pending id that has either got its title or timed out.
	// Returns whether anything is still left to wait for.
	const prunePending = (): boolean => {
		let remaining = false;
		setPendingDeadlines((prev) => {
			if (prev.size === 0) {
				return prev;
			}
			const now = Date.now();
			const next = new Map(prev);
			for (const [id, deadline] of prev) {
				if (hasTitle(id) || now >= deadline) {
					next.delete(id);
				}
			}
			remaining = next.size > 0;
			return next.size === prev.size ? prev : next;
		});
		return remaining;
	};

	const poll = async () => {
		if (!prunePending()) {
			stopPolling();
			return;
		}
		await refetch();
		if (!prunePending()) {
			stopPolling();
		}
	};

	/** Starts waiting (and, if needed, polling) for `document_id`'s title/summary to appear. */
	const markPendingMetadata = (document_id: string) => {
		if (hasTitle(document_id)) {
			return;
		}
		setPendingDeadlines((prev) => {
			const next = new Map(prev);
			next.set(document_id, Date.now() + PENDING_MAX_WAIT_MS);
			return next;
		});
		if (pollTimer === undefined) {
			pollTimer = setInterval(() => void poll(), PENDING_POLL_INTERVAL_MS);
		}
	};

	const isMetadataPending = (document_id: string | undefined) =>
		document_id !== undefined && pendingDeadlines().has(document_id);

	// Saving an artifact refers to it by its artifact ID (e.g. "ar_vs_..."),
	// which is a *different* identifier from the `public_id` the resulting
	// asset gets in this list - there's no way to map one to the other from
	// here. Instead, snapshot the ids present before refetching and treat
	// whichever untitled id(s) appear afterwards as the newly-saved item(s),
	// then track those (by their real `public_id`) for the pending poll.
	const refetchAndTrackNewSave = async () => {
		const idsBefore = new Set(
			artifactList()
				?.items?.map((item) => item.public_id)
				.filter((id): id is string => Boolean(id)) ?? [],
		);
		await refetch();
		for (const item of artifactList()?.items ?? []) {
			if (item.public_id && !item.title && !idsBefore.has(item.public_id)) {
				markPendingMetadata(item.public_id);
			}
		}
	};

	const refetchUntilNewItems = async () => {
		await refetchUntilItemCountIncreases({
			itemCountBefore: artifactList()?.items?.length ?? 0,
			refetch,
			maxAttempts: 3,
			retryMs: 1500,
			isAborted: () => refreshAborted,
		});
	};

	return {
		artifactList,
		toggleFavourite,
		isMetadataPending,
		refetchArtifacts: refetch,
		refetchAndTrackNewSave,
		refetchUntilNewItems,
	};
};
